import { IVec2, Vec2 } from "@base/math/Vec2";
import { IVec3 } from "@base/math/Vec3";

export class Barycentric2d {
  static fromPoint(p: IVec2, a: IVec2, b: IVec2, c: IVec2): IVec3 {
    const v0 = Vec2.to(a, b);
    const v1 = Vec2.to(a, c);
    const v2 = Vec2.to(a, p);

    const den = v0.x * v1.y - v1.x * v0.y;
    const v = (v2.x * v1.y - v1.x * v2.y) / den;
    const w = (v0.x * v2.y - v2.x * v0.y) / den;
    const u = 1.0 - v - w;
    return { x: u, y: v, z: w };
  }

  static toPoint(barycentric: { x: number; y: number; z: number }, a: IVec2, b: IVec2, c: IVec2): IVec2 {
    const { x: u, y: v, z: w } = barycentric;
    return {
      x: u * a.x + v * b.x + w * c.x,
      y: u * a.y + v * b.y + w * c.y,
    };
  }
}


export class Barycentric3d {
  static fromPoint(p: IVec3, a: IVec3, b: IVec3, c: IVec3): { x: number; y: number; z: number } {
    const v0 = { x: b.x - a.x, y: b.y - a.y, z: b.z - a.z };
    const v1 = { x: c.x - a.x, y: c.y - a.y, z: c.z - a.z };
    const v2 = { x: p.x - a.x, y: p.y - a.y, z: p.z - a.z };

    const dot00 = v0.x * v0.x + v0.y * v0.y + v0.z * v0.z;
    const dot01 = v0.x * v1.x + v0.y * v1.y + v0.z * v1.z;
    const dot02 = v0.x * v2.x + v0.y * v2.y + v0.z * v2.z;
    const dot11 = v1.x * v1.x + v1.y * v1.y + v1.z * v1.z;
    const dot12 = v1.x * v2.x + v1.y * v2.y + v1.z * v2.z;

    const invDenom = 1 / (dot00 * dot11 - dot01 * dot01);
    const v = (dot11 * dot02 - dot01 * dot12) * invDenom;
    const w = (dot00 * dot12 - dot01 * dot02) * invDenom;
    const u = 1 - v - w;

    return { x: u, y: v, z: w };
  }

  static toPoint(barycentric: { x: number; y: number; z: number }, a: IVec3, b: IVec3, c: IVec3): IVec3 {
    const { x: u, y: v, z: w } = barycentric;
    return {
      x: u * a.x + v * b.x + w * c.x,
      y: u * a.y + v * b.y + w * c.y,
      z: u * a.z + v * b.z + w * c.z,
    };
  }
}